import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import {useRect} from '@reach/rect'
import * as React from 'react'
import {useInView} from 'react-intersection-observer'
import styled, {css} from 'styled-components'

import PlayButton from '@/components/play-button'
import PopoverVideo from '@/components/popover-video'
import Image from '@helios/shared/components/image'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import {useMergedRefs} from '@helios/shared/hooks/use-merged-refs'
import {ArrowLeft} from '@helios/shared/images/icons/arrow-left'
import {ArrowRight} from '@helios/shared/images/icons/arrow-right'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Section} from '@helios/shared/styles/grid'
import {Text, TextLink} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'
import type ImageSource from '@helios/shared/types/image'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

interface Props {
    sliderHeading?: string
    highlightFirstCard?: boolean
    hideMediaOnMobile?: boolean
    cards: CardType[]
}

interface CardType {
    itemHeading?: string
    subHeading?: string
    description?: string
    subDescription?: string
    image?: ImageSource
    ctaText?: string
    ctaLink?: PrismicLinkProps
    videoId?: string
}

interface CardProps extends CardType {
    index: number
    setVisibleCards: React.Dispatch<React.SetStateAction<number[]>>
    hideMediaOnMobile: boolean
    isHighlighted: boolean
}

export const SliderCards = ({
    sliderHeading,
    highlightFirstCard = false,
    hideMediaOnMobile = false,
    cards,
}: Props) => {
    const cardsRefs = React.useRef<Array<HTMLLIElement | null>>([])
    const headingRef = React.useRef<HTMLParagraphElement | null>(null)
    const [visibleCards, setVisibleCards] = React.useState<number[]>([])
    const headingDimensions = useRect(headingRef)

    const tailCard = visibleCards[0]
    const headCard = visibleCards[visibleCards.length - 1] || 0

    const onGoToClick = (index: number) => {
        track(TrackingEvent.SliderCardsButtonActioned, {action: 'clicked'})

        cardsRefs.current?.[index]?.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
        })
    }

    return (
        <Wrapper>
            <HeaderWrapper>
                <Text ref={headingRef} variant="heading-base">
                    {sliderHeading}
                </Text>
            </HeaderWrapper>

            <Slider $paddingLeft={headingDimensions?.left}>
                {cards.map((card, index) => (
                    <Card
                        {...card}
                        key={index}
                        ref={(el) => (cardsRefs.current[index] = el)}
                        index={index}
                        setVisibleCards={setVisibleCards}
                        isHighlighted={highlightFirstCard && index === 0}
                        hideMediaOnMobile={hideMediaOnMobile}
                    />
                ))}
            </Slider>

            <ButtonsWrapper>
                <NavigationButton onClick={() => onGoToClick(tailCard)}>
                    <StyledArrowLeft />
                </NavigationButton>

                <NavigationButton onClick={() => onGoToClick(headCard)}>
                    <StyledArrowRight />
                </NavigationButton>
            </ButtonsWrapper>
        </Wrapper>
    )
}

const Card = React.forwardRef<HTMLLIElement | null, CardProps>(
    (
        {
            index,
            itemHeading,
            subHeading,
            description,
            subDescription,
            image,
            ctaText,
            ctaLink,
            videoId,
            isHighlighted,
            hideMediaOnMobile,
            setVisibleCards,
        },
        ref
    ) => {
        const [refView, inView] = useInView()
        const mergedRef = useMergedRefs([refView, ref])
        const globalStrings = useGlobalStrings()

        React.useEffect(() => {
            // If the card is in view, the visible cards array is updated with card's id)
            if (inView) {
                setVisibleCards((prev) =>
                    // Set is used here to prevent array elements from duplicating.
                    // The order of the elements depends on the scrolling direction, that's why the elements are sorted in ascending order
                    Array.from(new Set([...prev, index].sort((a, b) => a - b)))
                )
            } else {
                // if the card is not in view, remove it from the array
                setVisibleCards((prev) => [...prev].filter((el) => el !== index))
            }
        }, [index, inView, setVisibleCards])

        return (
            <StyledCard
                ref={mergedRef}
                $isHighlighted={isHighlighted}
                $hideOnMobile={hideMediaOnMobile}
            >
                <UpperWrapper>
                    {itemHeading && <Text variant="title-base">{itemHeading}</Text>}
                    {description && <Text>{description}</Text>}
                </UpperWrapper>

                <LowerWrapper>
                    {!!ctaLink && !!ctaText && (
                        <StyledLink
                            as={PrismicLink}
                            $color={isHighlighted ? colors.text.inverted : colors.text.default}
                            {...ctaLink}
                        >
                            {ctaText} →
                        </StyledLink>
                    )}
                    {subHeading && (
                        <Text
                            variant="paragraph-large-medium"
                            color={isHighlighted ? 'inverted' : 'muted'}
                        >
                            {subHeading}
                        </Text>
                    )}
                    {subDescription && (
                        <Text
                            variant="paragraph-large"
                            color={isHighlighted ? 'inverted' : 'muted'}
                        >
                            {subDescription}
                        </Text>
                    )}
                </LowerWrapper>

                {image?.url && (
                    <MediaWrapper $hideOnMobile={hideMediaOnMobile}>
                        <StyledImage image={image} width={500} />
                        {videoId && (
                            <PopoverVideo videoId={videoId}>
                                {({openPopover}) => (
                                    <StyledPlayButton
                                        onClick={openPopover}
                                        aria-label={globalStrings.play}
                                    />
                                )}
                            </PopoverVideo>
                        )}
                    </MediaWrapper>
                )}
            </StyledCard>
        )
    }
)

interface MediaWrapperProps {
    $hideOnMobile: boolean
}

interface StyledCardProps {
    $isHighlighted: boolean
    $hideOnMobile: boolean
}

interface StyledLinkProps {
    $color: typeof colors.text[keyof typeof colors.text]
}

interface SliderProps {
    $paddingLeft?: number
}

const MOBILE_BREAKPOINT = 'xs'

const HeaderWrapper = styled(Section)`
    padding-bottom: 0;
`

const ButtonsWrapper = styled(Section)`
    display: grid;
    grid-auto-flow: column;
    column-gap: ${tokens.spacing40};
    justify-content: center;
    padding-top: 0;

    ${mq.min(MOBILE_BREAKPOINT)} {
        justify-content: start;
    }
`

const MediaWrapper = styled.div<MediaWrapperProps>`
    position: relative;
    display: ${(props) => (props.$hideOnMobile ? 'none' : 'block')};
    grid-row: 3 / 4;

    ${mq.min(MOBILE_BREAKPOINT)} {
        display: block;
    }
`

const StyledPlayButton = styled(PlayButton)`
    position: absolute;
    top: 50%;
    left: 50%;
    width: 60px;
    height: 60px;
    transform: translate(-50%, -50%);

    ${mq.min(MOBILE_BREAKPOINT)} {
        width: 96px;
        height: 96px;
    }
`

const NavigationButton = styled.button`
    display: grid;
    width: 48px;
    height: 48px;
    color: ${colors.border.secondary};
    cursor: pointer;
    background-color: transparent;
    border: 1px solid ${colors.border.secondary};
    border-radius: ${radii.circle};
    place-items: center;

    :focus {
        ${focusStyles()}
        border: 1px solid transparent;
    }

    ${mq.min(MOBILE_BREAKPOINT)} {
        width: 72px;
        height: 72px;
    }
`

const arrowsStyles = css`
    width: 60%;

    ${mq.max(MOBILE_BREAKPOINT)} {
        /* iOS Safari doesn't handle percentage widths correctly */
        @supports (-webkit-backdrop-filter: blur(1px)) {
            width: 150%;
        }
    }
`

const StyledArrowLeft = styled(ArrowLeft)`
    ${arrowsStyles}
`
const StyledArrowRight = styled(ArrowRight)`
    ${arrowsStyles}
`

const Wrapper = styled.div`
    display: grid;
    grid-auto-flow: row;
    row-gap: ${tokens.spacing40};
`

const Slider = styled.ul<SliderProps>`
    display: grid;
    padding: 0;
    padding-right: ${tokens.spacing40};
    padding-left: ${(props) => (props.$paddingLeft ? props.$paddingLeft - 16 : 0)}px;
    margin: 0;
    overflow-x: auto;
    overflow-y: auto;
    grid-auto-flow: column;
    column-gap: ${tokens.spacing20};
    scroll-behavior: smooth;
    scrollbar-width: none;

    ::-webkit-scrollbar {
        display: none;
    }

    ${mq.min(MOBILE_BREAKPOINT)} {
        column-gap: ${tokens.spacing40};
    }
`

const StyledCard = styled.li<StyledCardProps>`
    display: grid;
    grid-template-rows: ${(props) => (props.$hideOnMobile ? '1fr 0.5fr' : '1fr 0.5fr 150px')};
    width: 35vw;
    min-width: 300px;
    align-content: space-between;
    padding: ${tokens.spacing28};
    color: ${(props) => (props.$isHighlighted ? colors.text.inverted : colors.text.default)};
    list-style: none;
    background-color: ${(props) =>
        props.$isHighlighted ? colors.background.darkGrey : colors.background.default};
    border: 1px solid ${colors.border.dark};
    row-gap: ${tokens.spacing12};

    ${mq.min(MOBILE_BREAKPOINT)} {
        width: 40vw;
        max-width: 500px;
        grid-template-rows: 1fr auto 240px;
    }
`

const StyledImage = styled(Image)`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
`

const StyledLink = styled(TextLink)<StyledLinkProps>`
    justify-self: start;
    color: ${(props) => props.$color};
`

const UpperWrapper = styled.div`
    display: grid;
    grid-auto-flow: row;
    row-gap: ${tokens.spacing12};
    grid-row: 1 / 2;
    align-self: start;
`

const LowerWrapper = styled.div`
    display: grid;
    grid-auto-flow: row;
    grid-row: 2 / 3;
    margin-bottom: ${tokens.spacing20};

    ${mq.min(MOBILE_BREAKPOINT)} {
        margin-bottom: ${tokens.spacing48};
    }
`
